import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Star,
  Loader2,
  AlertCircle,
  FileText,
  CheckCircle
} from 'lucide-react';
import {
  apiService,
  SummaryPromptCollection,
  SummaryFields,
  CreateSummaryCollectionRequest,
  UpdateSummaryCollectionRequest,
  SUMMARY_FIELD_DEFINITIONS,
  DEFAULT_SUMMARY_FIELDS,
} from '../../services/api';
import { toast } from 'sonner';

export function SummaryPromptCollectionsPage() {
  const [collections, setCollections] = useState<SummaryPromptCollection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState<SummaryPromptCollection | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<CreateSummaryCollectionRequest>({
    name: '',
    description: '',
    isSystemDefault: false,
    isActive: true,
    fields: { ...DEFAULT_SUMMARY_FIELDS },
  });

  useEffect(() => {
    loadCollections();
  }, []);

  const loadCollections = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.getSummaryCollections({ includeInactive: true });
      setCollections(response.items);
    } catch (err: any) {
      setError(err.message || 'Failed to load collections');
      toast.error('Failed to load summary collections');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCollection(null);
    setFormData({
      name: '',
      description: '',
      isSystemDefault: false,
      isActive: true,
      fields: { ...DEFAULT_SUMMARY_FIELDS },
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (collection: SummaryPromptCollection) => {
    setEditingCollection(collection);
    // Merge existing fields with defaults to handle missing fields (e.g., shortSummary)
    const mergedFields: SummaryFields = {
      ...DEFAULT_SUMMARY_FIELDS,
      ...collection.fields,
    };
    setFormData({
      name: collection.name,
      description: collection.description || '',
      isSystemDefault: collection.isSystemDefault,
      isActive: collection.isActive,
      fields: mergedFields,
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      if (editingCollection) {
        const updateData: UpdateSummaryCollectionRequest = {
          name: formData.name,
          description: formData.description,
          isActive: formData.isActive,
          fields: formData.fields,
        };
        await apiService.updateSummaryCollection(editingCollection.id, updateData);
        toast.success('Collection updated successfully');
      } else {
        await apiService.createSummaryCollection(formData);
        toast.success('Collection created successfully');
      }

      closeDialog();
      await loadCollections();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save collection');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await apiService.setSummaryCollectionAsDefault(id);
      toast.success('Default collection updated');
      await loadCollections();
    } catch (err: any) {
      toast.error(err.message || 'Failed to set default');
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this collection?')) {
      try {
        await apiService.deleteSummaryCollection(id);
        toast.success('Collection deleted successfully');
        await loadCollections();
      } catch (err: any) {
        toast.error(err.message || 'Failed to delete collection');
      }
    }
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingCollection(null);
    setFormData({
      name: '',
      description: '',
      isSystemDefault: false,
      isActive: true,
      fields: { ...DEFAULT_SUMMARY_FIELDS },
    });
  };

  const updateFieldConfig = (
    fieldKey: keyof SummaryFields,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      fields: {
        ...prev.fields,
        [fieldKey]: {
          ...prev.fields[fieldKey],
          prompt: value,
        },
      },
    }));
  };

  const filteredCollections = collections.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Summary Collections</h2>
          <p className="text-muted-foreground">
            Configure summary prompts for chat session analysis
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" /> New Collection
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 text-red-600 bg-red-50 rounded-lg">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search collections..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredCollections.map((collection) => (
          <Card key={collection.id} className={!collection.isActive ? 'opacity-60' : ''}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <CardTitle className="text-lg">{collection.name}</CardTitle>
                </div>
                <div className="flex items-center gap-1">
                  {collection.isSystemDefault && (
                    <Badge variant="default" className="bg-yellow-500">
                      <Star className="h-3 w-3 mr-1" /> Default
                    </Badge>
                  )}
                  {!collection.isActive && (
                    <Badge variant="secondary">Inactive</Badge>
                  )}
                </div>
              </div>
              {collection.description && (
                <CardDescription className="line-clamp-2">
                  {collection.description}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                <span className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4" />
                  7 fields
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => handleEdit(collection)}>
                  <Edit className="h-4 w-4 mr-1" /> Edit
                </Button>
                {!collection.isSystemDefault && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSetDefault(collection.id)}
                    >
                      <Star className="h-4 w-4 mr-1" /> Set Default
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleDelete(collection.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredCollections.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-2 text-lg font-medium">No collections found</h3>
          <p className="text-muted-foreground">
            {searchTerm
              ? 'Try a different search term'
              : 'Create your first summary collection'}
          </p>
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCollection ? 'Edit Collection' : 'Create Collection'}
            </DialogTitle>
            <DialogDescription>
              Configure the summary fields and their prompts
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., E-Commerce Support"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="active" className="flex items-center gap-2">
                  Active
                  <Switch
                    id="active"
                    checked={formData.isActive}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, isActive: checked })
                    }
                  />
                </Label>
                <p className="text-xs text-muted-foreground">
                  Inactive collections are hidden from selection
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description for this collection"
                rows={2}
              />
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-4">Field Configuration (7 fields)</h4>
              <div className="space-y-4">
                {(Object.keys(SUMMARY_FIELD_DEFINITIONS) as Array<keyof SummaryFields>).map(
                  (fieldKey) => {
                    const def = SUMMARY_FIELD_DEFINITIONS[fieldKey];
                    const config = formData.fields[fieldKey];
                    return (
                      <div
                        key={fieldKey}
                        className="p-4 border rounded-lg bg-white"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Label className="font-medium">{def.label}</Label>
                          <Badge variant="outline" className="text-xs">
                            {def.type}
                          </Badge>
                        </div>
                        <Textarea
                          value={config.prompt}
                          onChange={(e) =>
                            updateFieldConfig(fieldKey, e.target.value)
                          }
                          placeholder="Enter the prompt for this field..."
                          rows={2}
                          className="mt-2"
                        />
                      </div>
                    );
                  }
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isSaving || !formData.name.trim()}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editingCollection ? 'Save Changes' : 'Create Collection'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
